import { useEffect, useState, useRef, useContext } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { 
  ImpressumPage, AGBPage, DatenschutzPage, ForgotPasswordPage, ResetPasswordPage, 
  LandingPage, AuthPage as AuthPageComponent, SettingsPage as SettingsPageComponent,
  DashboardHome, JobsPage, CreateJobPage, JobDetailPage, BidsPage, MessagesPage, SubscriptionPage
} from "@/pages";
import CategoriesPage from "@/pages/CategoriesPage";
import PricingPage from "@/pages/PricingPage";
import AddRolePage from "@/pages/AddRolePage";
import NotificationsPage from "@/pages/NotificationsPage";
import AppointmentsPage from "@/pages/AppointmentsPage";
import NotFoundPage from "@/pages/NotFoundPage";
import { 
  AdminDashboard, AdminUserManagement, AdminUserDetails, 
  AdminJobManagement, AdminSupportTickets, AdminAnnouncements, AdminRegistrations,
  AdminAuditLogs, AdminBackup, AdminPayments, AdminPromoPage, AdminNotificationsPage, AdminAdsMarketing,
  AdminHeroSlides, AdminUserActivity
} from "@/pages/admin";
import DashboardLayout from "@/components/DashboardLayout";
import { GlobalAdTopBar } from "@/components/AdsBanner";
import { Loader2, CheckCircle, AlertCircle, Globe } from "lucide-react";
import { checkSubscriptionAccess } from "@/context/AppContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Setup axios defaults for better mobile compatibility
axios.defaults.timeout = 30000; // 30 second timeout
axios.defaults.headers.common['Content-Type'] = 'application/json';

// Setup axios interceptor to include token in all requests
axios.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${token}`;
    }
    // Always send cookies — required for the 3-layer persistent auth
    // (HttpOnly session_token cookie is the reliable fallback when
    // mobile browsers / TWA clear localStorage).
    config.withCredentials = true;
    return config;
  },
  (error) => Promise.reject(error)
);

// Add response interceptor to handle auth errors
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      const url = error.config?.url || '';
      if (!url.includes('/auth/login') && !url.includes('/auth/register') && !url.includes('/auth/me')) {
        console.log('Auth error on:', url);
      }
    }
    return Promise.reject(error);
  }
);

// ==================== Translations (loaded from JSON files) ====================
import de from "@/locales/de.json";
import en from "@/locales/en.json";
import fr from "@/locales/fr.json";
import it from "@/locales/it.json";
const translations = { de, en, fr, it };


// ==================== Contexts ====================
// Import contexts from AppContext.js (single source of truth)
import { AuthContext, LanguageContext } from "@/context/AppContext";

// ==================== Language Provider ====================
const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    return localStorage.getItem('language') || 'de';
  });

  // Listen for language sync events from AuthProvider (when user logs in)
  useEffect(() => {
    const handler = (e) => {
      if (e.detail?.lang && ['de','en','fr','it'].includes(e.detail.lang)) {
        setLanguage(e.detail.lang);
        localStorage.setItem('language', e.detail.lang);
      }
    };
    window.addEventListener('infirmly:language-sync', handler);
    return () => window.removeEventListener('infirmly:language-sync', handler);
  }, []);

  const t = (key) => {
    return translations[language]?.[key] || translations['de'][key] || key;
  };

  const changeLanguage = (lang) => {
    setLanguage(lang);
    localStorage.setItem('language', lang);
    // Persist to backend if user is logged in
    const token = localStorage.getItem('token');
    if (token) {
      axios.put(`${process.env.REACT_APP_BACKEND_URL}/api/users/profile`,
        { language: lang },
        { withCredentials: true, headers: token ? { Authorization: `Bearer ${token}` } : {} }
      ).catch(() => {});
    }
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage: changeLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

// ==================== Auth Provider ====================
const AuthProvider = ({ children }) => {
  // === LAYER 3: Instant load from cached user_data ===
  const [user, setUser] = useState(() => {
    try {
      const cached = localStorage.getItem('user_data');
      return cached ? JSON.parse(cached) : null;
    } catch {
      return null;
    }
  });
  const [loading, setLoading] = useState(true);

  // Decode JWT payload (no signature check — just used to read `exp` & `sub` locally)
  const decodeJwt = (token) => {
    try {
      if (!token || token.split('.').length !== 3) return null;
      const payload = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
      const padded = payload + '==='.slice((payload.length + 3) % 4);
      return JSON.parse(decodeURIComponent(atob(padded).split('').map(
        c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)
      ).join('')));
    } catch { return null; }
  };

  const isTokenStillValid = (token) => {
    const payload = decodeJwt(token);
    if (!payload || !payload.exp) return false;
    // exp is in seconds since epoch
    return payload.exp * 1000 > Date.now();
  };

  // Persist user data whenever it changes (Layer 3 cache)
  useEffect(() => {
    try {
      if (user) localStorage.setItem('user_data', JSON.stringify(user));
      else localStorage.removeItem('user_data');
    } catch {}
  }, [user]);

  // Identify user to OneSignal when they log in
  useEffect(() => {
    const identifyUserToOneSignal = async () => {
      if (!user || !window.OneSignal) return;
      
      try {
        window.OneSignalDeferred = window.OneSignalDeferred || [];
        window.OneSignalDeferred.push(async function(OneSignal) {
          await OneSignal.login(user.user_id);
          await OneSignal.User.addTags({
            role: user.role || 'client',
            email: user.email,
            name: user.name,
          });
          console.log('User identified to OneSignal:', user.user_id);
        });
      } catch (error) {
        console.error('Failed to identify user to OneSignal:', error);
      }
    };
    
    identifyUserToOneSignal();
  }, [user]);

  // === Helpers for Layer 2 (non-HttpOnly cookie backup, readable by JS) ===
  const setAuthCookie = (token) => {
    try {
      const isHttps = window.location.protocol === 'https:';
      // On HTTPS we must use SameSite=None + Secure so the cookie is
      // accepted/sent in cross-origin (frontend ↔ API) contexts (TWA, PWA,
      // separate API domain). On plain http (local dev) fall back to Lax.
      const attrs = isHttps
        ? `; SameSite=None; Secure`
        : `; SameSite=Lax`;
      document.cookie = `auth_token=${token}; path=/; max-age=${365*24*60*60}${attrs}`;
    } catch {}
  };
  const getAuthCookie = () => {
    try {
      const m = document.cookie.match(/(?:^|;\s*)auth_token=([^;]+)/);
      return m ? m[1] : null;
    } catch { return null; }
  };
  const clearAuthCookie = () => {
    try { document.cookie = 'auth_token=; path=/; max-age=0'; } catch {}
  };

  const getToken = () => {
    try { return localStorage.getItem('token') || getAuthCookie(); } catch { return getAuthCookie(); }
  };

  const checkAuth = async () => {
    try {
      const storedToken = getToken();
      const cachedData = localStorage.getItem('user_data');

      // Restore localStorage from JS-readable cookie if needed
      if (!localStorage.getItem('token') && storedToken) {
        try { localStorage.setItem('token', storedToken); } catch {}
      }

      // Always try /auth/me — the backend HttpOnly session_token cookie
      // can authenticate us even when localStorage was wiped.
      const headers = storedToken ? { Authorization: `Bearer ${storedToken}` } : {};
      const res = await axios.get(`${API}/auth/me`, { withCredentials: true, headers });

      setUser(res.data);

      // Sync language from user profile
      if (res.data.language && ['de','en','fr','it'].includes(res.data.language)) {
        window.dispatchEvent(new CustomEvent('infirmly:language-sync', { detail: { lang: res.data.language } }));
      }

      // Auto-refresh token if backend says so
      if (res.data.refreshed_token) {
        try { localStorage.setItem('token', res.data.refreshed_token); } catch {}
        setAuthCookie(res.data.refreshed_token);
        console.log('[Auth] Token auto-refreshed');
      } else if (res.data.token) {
        try { localStorage.setItem('token', res.data.token); } catch {}
        setAuthCookie(res.data.token);
      }
    } catch (error) {
      console.warn('[Auth] /auth/me failed:', error.response?.status, error.message);
      const cachedData = localStorage.getItem('user_data');
      const storedToken = getToken();

      // === Resilient logout policy ===
      // We logout ONLY when ALL of these are true:
      //   1. Backend explicitly returned 401 (token rejected, not network/timeout)
      //   2. We have no cached user_data
      //   3. We have no still-valid JWT (exp in the past) anywhere in storage
      //
      // This survives the iOS Safari / TWA ITP storage wipe scenario:
      // if the HttpOnly cookie was dropped by the browser but the JWT in
      // localStorage is still good for the next 364 days, we trust it and
      // keep the user signed in. Future requests will use the Bearer header.
      const tokenStillValid = isTokenStillValid(storedToken);

      if (error.response?.status === 401 && !cachedData && !tokenStillValid) {
        // Truly unauthenticated — wipe and force re-login
        try { localStorage.removeItem('token'); localStorage.removeItem('user_data'); } catch {}
        clearAuthCookie();
        setUser(null);
      } else if (cachedData && !user) {
        // Restore from cache (initial network failure but cache still good)
        try { setUser(JSON.parse(cachedData)); } catch {}
      } else if (!cachedData && tokenStillValid && !user) {
        // localStorage was wiped (iOS ITP) but the JWT is still valid.
        // Reconstruct a minimal user from the JWT payload so the app
        // doesn't bounce to /login. Real user data will refresh on the
        // next successful /auth/me call.
        const payload = decodeJwt(storedToken);
        if (payload?.sub) {
          setUser({ user_id: payload.sub, _restored_from_jwt: true });
          // Make sure the token is back in localStorage so future
          // requests carry the Authorization header.
          try { localStorage.setItem('token', storedToken); } catch {}
          setAuthCookie(storedToken);
        }
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    checkAuth();
    // Background refresh when tab regains visibility (5s debounce)
    let debounceTimer = null;
    const onVisibility = () => {
      if (document.visibilityState === 'visible' && getToken()) {
        clearTimeout(debounceTimer);
        debounceTimer = setTimeout(() => checkAuth(), 5000);
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      clearTimeout(debounceTimer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = (userData, token) => {
    console.log('[Auth] Login called with token:', token ? 'YES' : 'NO');
    setUser(userData);
    if (userData?.language && ['de','en','fr','it'].includes(userData.language)) {
      window.dispatchEvent(new CustomEvent('infirmly:language-sync', { detail: { lang: userData.language } }));
    }
    if (token) {
      try { localStorage.setItem('token', token); } catch {}
      setAuthCookie(token);
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (e) {
      console.error('Logout API error:', e);
    }
    
    if (window.OneSignal) {
      try {
        window.OneSignalDeferred = window.OneSignalDeferred || [];
        window.OneSignalDeferred.push(async function(OneSignal) {
          await OneSignal.logout();
        });
      } catch (e) {
        console.error('OneSignal logout failed:', e);
      }
    }
    try { localStorage.removeItem('token'); localStorage.removeItem('user_data'); } catch {}
    clearAuthCookie();
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, setUser, login, logout, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

// ==================== Context Hooks ====================
const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};

const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) throw new Error('useLanguage must be used within LanguageProvider');
  return context;
};

// ==================== Language Selector Component ====================
const LanguageSelector = () => {
  const { language, setLanguage } = useLanguage();
  
  return (
    <Select value={language} onValueChange={setLanguage}>
      <SelectTrigger className="w-[120px] border-gray-200" data-testid="language-selector">
        <Globe className="w-4 h-4 mr-2" />
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="de">Deutsch</SelectItem>
        <SelectItem value="en">English</SelectItem>
        <SelectItem value="fr">Français</SelectItem>
        <SelectItem value="it">Italiano</SelectItem>
      </SelectContent>
    </Select>
  );
};

// ==================== Auth Callback ====================
const AuthCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = location.hash;
    const sessionId = hash.split('session_id=')[1];

    if (sessionId) {
      axios.post(`${API}/auth/session`, { session_id: sessionId }, { withCredentials: true })
        .then(res => {
          login(res.data.user, res.data.token);
          navigate('/dashboard', { state: { user: res.data.user }, replace: true });
        })
        .catch(err => {
          console.error('Auth callback error:', err);
          toast.error("Anmeldung fehlgeschlagen");
          navigate('/login', { replace: true });
        });
    } else {
      navigate('/login', { replace: true });
    }
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center">
      <Loader2 className="w-8 h-8 animate-spin text-[#0044CC]" />
    </div>
  );
};

// ==================== Subscription Callback ====================
const SubscriptionCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, setUser } = useAuth();
  const [status, setStatus] = useState('verifying');
  const hasVerified = useRef(false);

  useEffect(() => {
    const verifyPayment = async () => {
      // Prevent double verification
      if (hasVerified.current) return;
      hasVerified.current = true;
      
      const searchParams = new URLSearchParams(location.search);
      const paymentStatus = searchParams.get('status');
      const plan = searchParams.get('plan');
      const paymentToken = localStorage.getItem('pending_payment_token');

      // If payment failed from Saferpay
      if (paymentStatus === 'failed') {
        setStatus('failed');
        localStorage.removeItem('pending_payment_token');
        localStorage.removeItem('pending_payment_plan');
        return;
      }

      // First, always try to verify with the token if we have it
      if (paymentToken) {
        try {
          const response = await axios.post(
            `${API}/saferpay/verify`,
            { token: paymentToken },
            { withCredentials: true }
          );

          if (response.data.success) {
            setStatus('success');
            toast.success('Abonnement erfolgreich aktiviert!');
            localStorage.removeItem('pending_payment_token');
            localStorage.removeItem('pending_payment_plan');
            
            if (user) {
              setUser({
                ...user,
                subscription: plan || response.data.type?.replace('subscription_', ''),
                subscription_expires: response.data.expires
              });
            }
            
            setTimeout(() => navigate('/dashboard'), 2000);
            return;
          }
        } catch (error) {
          console.error('Token verification failed:', error);
          // Continue to fallback methods
        }
      }

      // Fallback: Check if user already has active subscription (payment completed via webhook)
      try {
        const res = await axios.get(`${API}/auth/me`, { withCredentials: true });
        if (res.data.subscription) {
          setStatus('success');
          toast.success('Abonnement erfolgreich aktiviert!');
          localStorage.removeItem('pending_payment_token');
          localStorage.removeItem('pending_payment_plan');
          
          if (user) {
            setUser({
              ...user,
              subscription: res.data.subscription,
              subscription_expires: res.data.subscription_expires
            });
          }
          
          setTimeout(() => navigate('/dashboard'), 2000);
          return;
        }
      } catch (e) {
        console.error('Failed to check subscription status:', e);
      }

      // Fallback 2: Try to verify latest pending payment for this user
      try {
        const response = await axios.post(
          `${API}/saferpay/verify-latest`,
          {},
          { withCredentials: true }
        );

        if (response.data.success) {
          setStatus('success');
          toast.success('Abonnement erfolgreich aktiviert!');
          localStorage.removeItem('pending_payment_token');
          localStorage.removeItem('pending_payment_plan');
          
          if (user) {
            setUser({
              ...user,
              subscription: response.data.type?.replace('subscription_', ''),
              subscription_expires: response.data.expires
            });
          }
          
          setTimeout(() => navigate('/dashboard'), 2000);
          return;
        }
      } catch (e) {
        console.error('Latest payment verification failed:', e);
      }

      // If all else fails
      setStatus('failed');
      localStorage.removeItem('pending_payment_token');
      localStorage.removeItem('pending_payment_plan');
    };

    verifyPayment();
  }, [location, navigate, user, setUser]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <Card className="max-w-md w-full">
        <CardContent className="p-8 text-center">
          {status === 'verifying' && (
            <>
              <Loader2 className="w-16 h-16 mx-auto text-[#0044CC] animate-spin" />
              <h2 className="text-2xl font-bold mt-6" style={{ fontFamily: 'Chivo, sans-serif' }}>
                Zahlung wird überprüft...
              </h2>
              <p className="text-gray-600 mt-2">
                Bitte warten Sie, während wir Ihre Zahlung bestätigen.
              </p>
            </>
          )}

          {status === 'success' && (
            <>
              <CheckCircle className="w-16 h-16 mx-auto text-green-500" />
              <h2 className="text-2xl font-bold mt-6 text-green-600" style={{ fontFamily: 'Chivo, sans-serif' }}>
                Zahlung erfolgreich!
              </h2>
              <p className="text-gray-600 mt-2">Ihr Abonnement wurde aktiviert.</p>
              <p className="text-sm text-gray-500 mt-4">Weiterleitung zum Dashboard...</p>
            </>
          )}

          {status === 'failed' && (
            <>
              <AlertCircle className="w-16 h-16 mx-auto text-red-500" />
              <h2 className="text-2xl font-bold mt-6 text-red-600" style={{ fontFamily: 'Chivo, sans-serif' }}>
                Zahlung fehlgeschlagen
              </h2>
              <p className="text-gray-600 mt-2">Ihre Zahlung konnte nicht verarbeitet werden.</p>
              <Button
                onClick={() => navigate('/dashboard/subscription')}
                className="mt-6 bg-[#0044CC] hover:bg-[#003399] text-white rounded-full"
              >
                Erneut versuchen
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// ==================== Protected Route ====================
const ProtectedRoute = ({ children, requireSubscription = false }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#0044CC]" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check subscription access for providers on protected routes
  if (requireSubscription && user.role === 'provider') {
    const accessCheck = checkSubscriptionAccess(user);
    
    if (!accessCheck.hasAccess) {
      // Redirect to subscription page with status info
      return <Navigate to="/dashboard/subscription" state={{ 
        blocked: true, 
        status: accessCheck.status,
        reason: accessCheck.reason 
      }} replace />;
    }
  }

  return children;
};

// Provider route - requires subscription for full access
const ProviderProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#0044CC]" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Only check subscription for providers
  if (user.role === 'provider') {
    const accessCheck = checkSubscriptionAccess(user);
    
    if (!accessCheck.hasAccess) {
      // Allow access to subscription page only
      if (location.pathname === '/dashboard/subscription') {
        return children;
      }
      // Redirect to subscription page
      return <Navigate to="/dashboard/subscription" state={{ 
        blocked: true, 
        status: accessCheck.status,
        reason: accessCheck.reason 
      }} replace />;
    }
  }

  return children;
};

// Redirect admin users to Admin Dashboard
const AdminRedirect = ({ children }) => {
  const { user } = useAuth();
  
  if (user?.is_admin) {
    return <Navigate to="/admin" replace />;
  }
  
  return children;
};

// Landing Page Wrapper
const LandingPageWrapper = () => {
  const { t } = useLanguage();
  const { language } = useLanguage();
  return <LandingPage t={t} language={language} LanguageSelector={LanguageSelector} />;
};

const CategoriesPageWrapper = () => {
  const { language } = useLanguage();
  const navigate = useNavigate();
  
  // Always show the clean public-style categories page (no dashboard sidebar),
  // regardless of authentication state.
  return (
    <div className="min-h-screen infirmly-dark" style={{ background: "radial-gradient(ellipse at top, #0F2557 0%, #0A1A3E 60%, #050B23 100%)" }}>
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-2 text-blue-100/70 hover:text-white mb-6 text-sm font-medium transition-colors"
          data-testid="public-categories-back-btn"
        >
          ← {language === "de" ? "Zurück" : language === "fr" ? "Retour" : language === "it" ? "Indietro" : "Back"}
        </button>
        <CategoriesPage language={language} />
      </div>
    </div>
  );
};

// Auth Page Wrapper
const AuthPageWrapper = ({ mode }) => {
  return <AuthPageComponent mode={mode} useAuth={useAuth} useLanguage={useLanguage} />;
};

// Settings Page Wrapper
const SettingsPageWrapper = () => {
  return <SettingsPageComponent useAuth={useAuth} useLanguage={useLanguage} />;
};

// Admin Dashboard Wrapper
const AdminDashboardWrapper = () => {
  const { logout } = useAuth();
  return <AdminDashboard onLogout={logout} />;
};

// ==================== App Router ====================
function AppRouter() {
  const location = useLocation();

  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<LandingPageWrapper />} />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/categories" element={<CategoriesPageWrapper />} />
      <Route path="/login" element={<AuthPageWrapper mode="login" />} />
      <Route path="/register" element={<AuthPageWrapper mode="register" />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/subscription-callback" element={<SubscriptionCallback />} />
      <Route path="/admin" element={<ProtectedRoute><AdminDashboardWrapper /></ProtectedRoute>} />
      <Route path="/admin/registrations" element={<ProtectedRoute><AdminRegistrations /></ProtectedRoute>} />
      <Route path="/admin/users" element={<ProtectedRoute><AdminUserManagement /></ProtectedRoute>} />
      <Route path="/admin/users/:userId" element={<ProtectedRoute><AdminUserDetails /></ProtectedRoute>} />
      <Route path="/admin/jobs" element={<ProtectedRoute><AdminJobManagement /></ProtectedRoute>} />
      <Route path="/admin/support" element={<ProtectedRoute><AdminSupportTickets /></ProtectedRoute>} />
      <Route path="/admin/announcements" element={<ProtectedRoute><AdminAnnouncements /></ProtectedRoute>} />
      <Route path="/admin/audit-logs" element={<ProtectedRoute><AdminAuditLogs /></ProtectedRoute>} />
      <Route path="/admin/backup" element={<ProtectedRoute><AdminBackup /></ProtectedRoute>} />
      <Route path="/admin/payments" element={<ProtectedRoute><AdminPayments /></ProtectedRoute>} />
      <Route path="/admin/promo" element={<ProtectedRoute><AdminPromoPage /></ProtectedRoute>} />
      <Route path="/admin/notifications" element={<ProtectedRoute><AdminNotificationsPage /></ProtectedRoute>} />
      <Route path="/admin/ads" element={<ProtectedRoute><AdminAdsMarketing /></ProtectedRoute>} />
      <Route path="/admin/hero-slides" element={<ProtectedRoute><AdminHeroSlides /></ProtectedRoute>} />
      <Route path="/admin/user-activity" element={<ProtectedRoute><AdminUserActivity /></ProtectedRoute>} />
      <Route path="/impressum" element={<ImpressumPage />} />
      <Route path="/agb" element={<AGBPage />} />
      <Route path="/datenschutz" element={<DatenschutzPage />} />
      <Route
        path="/dashboard"
        element={
          <ProviderProtectedRoute>
            <AdminRedirect>
              <DashboardLayout>
                <DashboardHome />
              </DashboardLayout>
            </AdminRedirect>
          </ProviderProtectedRoute>
        }
      />
      <Route
        path="/dashboard/jobs"
        element={
          <ProviderProtectedRoute>
            <DashboardLayout>
              <JobsPage />
            </DashboardLayout>
          </ProviderProtectedRoute>
        }
      />
      <Route
        path="/dashboard/jobs/new"
        element={
          <ProviderProtectedRoute>
            <DashboardLayout>
              <CreateJobPage />
            </DashboardLayout>
          </ProviderProtectedRoute>
        }
      />
      <Route
        path="/dashboard/jobs/:jobId"
        element={
          <ProviderProtectedRoute>
            <DashboardLayout>
              <JobDetailPage />
            </DashboardLayout>
          </ProviderProtectedRoute>
        }
      />
      <Route
        path="/dashboard/bids"
        element={
          <ProviderProtectedRoute>
            <DashboardLayout>
              <BidsPage />
            </DashboardLayout>
          </ProviderProtectedRoute>
        }
      />
      <Route
        path="/dashboard/messages"
        element={
          <ProviderProtectedRoute>
            <DashboardLayout>
              <MessagesPage />
            </DashboardLayout>
          </ProviderProtectedRoute>
        }
      />
      <Route
        path="/dashboard/subscription"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <SubscriptionPage />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/settings"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <SettingsPageWrapper />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/add-role"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <AddRolePage />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/notifications"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <NotificationsPage />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/appointments"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <AppointmentsPage />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}

// ==================== Main App ====================
function App() {
  useEffect(() => {
    // Hide splash screen once React app is mounted
    if (window.hideSplash) {
      window.hideSplash();
    }
  }, []);

  return (
    <BrowserRouter>
      <LanguageProvider>
        <AuthProvider>
          <GlobalAdTopBar />
          <AppRouter />
          <Toaster position="top-right" richColors />
        </AuthProvider>
      </LanguageProvider>
    </BrowserRouter>
  );
}

export default App;
